import React, { createContext, useContext, useEffect, useState } from 'react';

// Create a context for the search provider
const SearchContext = createContext(null);

/**
 * Hook to use the search context
 * @returns {Object} The search context
 */
export function useSearch() {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
}

/**
 * Base SearchProvider component that creates a context with the provider implementation
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {Object} props.provider - The implementation of the search provider
 * @param {Object} props.settings - Settings for the search provider
 * @param {string} props.searchType - Type of search ('search', 'plp', 'header')
 * @returns {JSX.Element} A context provider for search functionality
 */
export function SearchProvider({ children, provider, settings, searchType = 'search' }) {
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize the provider when the component mounts
  useEffect(() => {
    let cleanupFunction = () => {};
    const initializeProvider = async () => {
      // Assuming initialize might be async or return a cleanup function
      const cleanup = await provider.initialize(settings);
      if (typeof cleanup === 'function') {
        cleanupFunction = cleanup;
      }
      setIsInitialized(true); // Signal that initialization is complete
    };

    initializeProvider();

    // Call cleanup function if it exists when the component unmounts
    return () => {
      cleanupFunction();
      setIsInitialized(false); // Reset initialization status on unmount
    };
  }, [provider, settings]);

  // Create the context value with provider methods, settings, and initialization status
  const contextValue = {
    settings,
    searchType,
    isInitialized, // Add initialization status to context
    ...provider
  };

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  );
}

export default SearchProvider;